import HiddenInput from "@/components/form/components/HiddenInput";
import { InputNumber } from "@/components/InputNumber";
import Ellipsis from "@/components/views/Ellipsis";
import { Decimal } from "@/utils/decimal";
import { formatLocaleCurrency } from "@/utils/formatUtils";
import { resolveFormValidationError, setErrorsToForm } from "@/utils/formUtils";
import { tInterval } from "@/utils/translationUtils";
import { isDefined, numberOrZero } from "@/utils/utils";
import { validations } from "@/utils/validationUtils";
import { Alert, App as AntApp, Col, Divider, Form, Input, Modal, Row } from "antd";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { DEFAULT_LOCALE } from "../../../../../app/i18n";
import { useUpdateCalculatedCommissions, useUpdateSplitCalculatedCommissions } from "../../../../../common/api/queries";
import { ModalSizes, rowGutter } from "../../../../../common/constants";
import { UUID } from "../../../../../typings/global";
import { formatAgentName } from "../../../../agent/utils";
import { useIsCurrentAgentTypePlus } from "../../../../auth/hooks";
import type { SplitCalculatedCommission } from "../../../splitcommissions/types";
import type { CalculatedCommission, UpdateCalculatedCommission, UpdateCalculatedCommissionList } from "../../types";

const calculateCommissionsAmount = (calculatedCommissions: CalculatedCommission[] | UpdateCalculatedCommission[]) => {
  let sum = 0;
  calculatedCommissions.forEach((calculatedCommission) => {
    sum = Decimal.sum(sum, numberOrZero(calculatedCommission.commissionAmount), 6);
  });

  return sum;
};

const colSpan = 6;

interface Props {
  open: boolean;
  onFormCancel: () => void;
  finalCommissionAmount?: number;
  calculatedCommissions: CalculatedCommission[] | SplitCalculatedCommission[];
  type: "calculated" | "splitCalculated";
  batchId?: UUID;
  itemId: UUID;
}

export const CalculatedCommissionsModalForm = ({
  open,
  onFormCancel,
  finalCommissionAmount,
  calculatedCommissions,
  type,
  batchId,
  itemId,
}: Props) => {
  const { t } = useTranslation();
  const { notification } = AntApp.useApp();

  const [form] = Form.useForm<UpdateCalculatedCommissionList>();
  const formCalculatedCommissions = Form.useWatch("calculatedCommissions", form);

  const isTopAgentPlus = useIsCurrentAgentTypePlus();

  const [currentCommissionsAmount, setCurrentCommissionsAmount] = useState<number>();

  const updateSplitCalculatedCommissions = useUpdateSplitCalculatedCommissions();
  const updateCalculatedCommissions = useUpdateCalculatedCommissions();

  const hasAmountsSumError =
    !!formCalculatedCommissions && finalCommissionAmount !== calculateCommissionsAmount(formCalculatedCommissions);

  const topAgentId = calculatedCommissions[0]?.gainer.id;
  const dividerPositions = calculatedCommissions.reduce<number[]>((positions, commission, index) => {
    if (commission?.gainer.id === topAgentId) {
      positions.push(index);
    }
    return positions;
  }, []);

  useEffect(() => {
    if (open) {
      form.setFieldsValue({
        calculatedCommissions: calculatedCommissions.map((calculatedCommission) => ({
          id: calculatedCommission.id,
          optimisticLockVersion: calculatedCommission.optimisticLockVersion,
          index: calculatedCommission.index,
          commissionAmount: calculatedCommission.commissionAmount,
          modified: calculatedCommission.modified || calculatedCommission.errors.length > 0,
          modificationReason: calculatedCommission.modificationReason,
        })),
      });
    }
  }, [open, calculatedCommissions, form, finalCommissionAmount]);

  const handleCommissionAmountChange = (commissionAmount: string | number, index: number) => {
    const calculatedCommission = calculatedCommissions[index];
    const modified =
      calculatedCommission?.modified ||
      calculatedCommission?.commissionAmount !== commissionAmount ||
      calculatedCommission?.errors.length > 0;

    const values = [...form.getFieldsValue().calculatedCommissions];
    if (values[index]?.modified !== modified) {
      values[index] = {
        ...(values[index] as UpdateCalculatedCommission),
        modified,
        modificationReason: modified ? (values[index]?.modificationReason as string) : "",
      };
      form.setFieldsValue({ calculatedCommissions: values as UpdateCalculatedCommission[] });
    }

    const calculatedCommissionsAmount = calculateCommissionsAmount(values);

    setCurrentCommissionsAmount(
      finalCommissionAmount !== calculatedCommissionsAmount ? calculatedCommissionsAmount : undefined,
    );
  };

  const handleFormSubmit = () => {
    form
      .validateFields()
      .then((values) => {
        if (hasAmountsSumError) {
          notification.error({
            title: t("common.error"),
            description: t("commissions.batch.validations.commissionAmountsSum", {
              commissionAmount: isDefined(finalCommissionAmount)
                ? formatLocaleCurrency(finalCommissionAmount)
                : undefined,
              currentCommissionsAmount: isDefined(currentCommissionsAmount)
                ? formatLocaleCurrency(currentCommissionsAmount)
                : undefined,
              commissionAmountDifference: formatLocaleCurrency(
                numberOrZero(finalCommissionAmount) - numberOrZero(currentCommissionsAmount),
              ),
            }),
          });

          return;
        }

        if (type === "splitCalculated") {
          updateSplitCalculatedCommissions.mutate(
            { id: itemId, object: values },
            {
              onError: (error) =>
                setErrorsToForm(form, "commissions.batch.attrs.commission.calculatedCommissions", error.violations),
              onSuccess: onFormCancel,
            },
          );
        } else if (type === "calculated" && batchId) {
          updateCalculatedCommissions.mutate(
            { id1: batchId, id2: itemId, object: values },
            {
              onError: (error) =>
                setErrorsToForm(form, "commissions.batch.attrs.commission.calculatedCommissions", error.violations),
              onSuccess: onFormCancel,
            },
          );
        }
      })
      .catch(resolveFormValidationError);
  };

  return (
    <Modal
      width={ModalSizes.LARGE}
      open={open}
      title={t("commissions.batch.titles.updateCalculatedCommissions")}
      okText={t("common.save")}
      cancelText={t("common.cancel")}
      confirmLoading={updateCalculatedCommissions.isPending || updateSplitCalculatedCommissions.isPending}
      afterClose={() => form.resetFields(["calculatedCommissions"])}
      onOk={handleFormSubmit}
      onCancel={onFormCancel}
      mask={{ closable: false }}
    >
      <Form form={form} layout="vertical" name="calculatedCommissionsForm">
        <Form.List name={["calculatedCommissions"]}>
          {(fields) => (
            <>
              {fields.map(({ key, ...field }) => (
                <React.Fragment key={key}>
                  <HiddenInput name={[field.name, "id"]} />
                  <HiddenInput name={[field.name, "optimisticLockVersion"]} />
                  <HiddenInput name={[field.name, "index"]} />
                  <HiddenInput name={[field.name, "modified"]} initialValue={false} />

                  {dividerPositions.indexOf(field.name) >= 0 && (
                    <Row gutter={rowGutter}>
                      <Col span={24}>
                        <Divider titlePlacement="center" style={{ marginTop: 12, marginBottom: 8 }}>
                          {tInterval(
                            `contract.attrs.gainerRecords.gainerId_interval`,
                            dividerPositions.indexOf(field.name) + 1,
                          )}
                        </Divider>
                      </Col>
                    </Row>
                  )}

                  <Row gutter={rowGutter}>
                    <Col span={colSpan} style={{ marginTop: 36 }}>
                      <b>
                        <Ellipsis>{formatAgentName(calculatedCommissions[field.name]?.gainer)}</Ellipsis>
                      </b>
                    </Col>

                    <Col span={colSpan}>
                      <Form.Item
                        {...field}
                        name={[field.name, "commissionAmount"]}
                        label={t("commissions.batch.attrs.commission.calculatedCommissions.commissionAmount")}
                        rules={[validations.notNull]}
                      >
                        <InputNumber
                          type="decimal"
                          addon="euro"
                          onChange={(value) =>
                            isDefined(value) ? handleCommissionAmountChange(value, field.name) : undefined
                          }
                          {...(isTopAgentPlus
                            ? {
                                precision: 6,
                                formatter: (value) => {
                                  if (!value) {
                                    return "";
                                  }

                                  return Intl.NumberFormat(DEFAULT_LOCALE, {
                                    minimumFractionDigits: 0,
                                    maximumFractionDigits: 6,
                                  }).format(value);
                                },
                              }
                            : undefined)}
                        />
                      </Form.Item>
                    </Col>

                    <Col span={colSpan * 2}>
                      <Form.Item
                        noStyle
                        shouldUpdate={(prev, next) =>
                          prev.calculatedCommissions[field.name].modified !==
                          next.calculatedCommissions[field.name].modified
                        }
                      >
                        {({ getFieldValue }) =>
                          getFieldValue(["calculatedCommissions", field.name, "modified"]) && (
                            <Form.Item
                              {...field}
                              name={[field.name, "modificationReason"]}
                              label={t("commissions.batch.attrs.commission.calculatedCommissions.modificationReason")}
                              rules={[validations.notBlank, validations.size(1, 255)]}
                            >
                              <Input />
                            </Form.Item>
                          )
                        }
                      </Form.Item>
                    </Col>
                  </Row>
                </React.Fragment>
              ))}
            </>
          )}
        </Form.List>
      </Form>

      {hasAmountsSumError && (
        <Row gutter={rowGutter}>
          <Col span={24}>
            <Alert
              type="error"
              showIcon
              title={t("commissions.batch.validations.commissionAmountsSum", {
                commissionAmount: isDefined(finalCommissionAmount)
                  ? formatLocaleCurrency(finalCommissionAmount, isTopAgentPlus ? 6 : 2)
                  : undefined,
                currentCommissionsAmount: isDefined(currentCommissionsAmount)
                  ? formatLocaleCurrency(currentCommissionsAmount, isTopAgentPlus ? 6 : 2)
                  : undefined,
                commissionAmountDifference: formatLocaleCurrency(
                  numberOrZero(finalCommissionAmount) - numberOrZero(currentCommissionsAmount),
                  isTopAgentPlus ? 6 : 2,
                ),
              })}
            />
          </Col>
        </Row>
      )}
    </Modal>
  );
};
